import type { Path } from '@exam-management/router';
import { usePermission } from '@shared/permissions';
import type { PropsWithChildren } from '@shared/utils';
import { matchPath, Navigate, useLocation, type PathParam } from 'react-router-dom';

export interface PermissionRouteGuardData {
  requiredPermission: string[] | string;
  path: Path;
  params?: {
    [key in PathParam<Path>]?: string | null;
  };
}

interface PermissionRouteGuardProps extends PropsWithChildren {
  routes: PermissionRouteGuardData[];
}

export default function PermissionRouteGuard(props: PermissionRouteGuardProps) {
  const { routes, children } = props;
  const { permissions } = usePermission();
  const location = useLocation();
  const { pathname } = location;
  const matchedRoute = routes.find(({ path, requiredPermission }) => {
    const requiredPermissionsArray = Array.isArray(requiredPermission)
      ? requiredPermission
      : [requiredPermission];

    // NOTED: No required permission at all
    if (matchPath(path, pathname) && requiredPermissionsArray.length === 0) {
      return true;
    }

    return (
      matchPath(path, pathname) &&
      requiredPermissionsArray.some((permission) => permissions.includes(permission))
    );
  });

  if (matchedRoute) {
    return children;
  }

  return <Navigate to='/access-denied' replace />;
}
